import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';

/**
 * API 统一响应格式。
 */
interface ApiResponse<T> {
  code: number;
  data: T;
  message: string;
  detail?: unknown;
}

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Axios 实例，配置基础 URL、拦截器和错误处理。
 */
const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('deepflow_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// 响应拦截器：提取 {code, data, message} 中的 data
apiClient.interceptors.response.use(
  (response: AxiosResponse<ApiResponse<unknown>>) => {
    const apiData = response.data;
    if (apiData.code !== 0 && apiData.code !== 200) {
      return Promise.reject(new Error(apiData.message || '请求失败'));
    }
    // 将 data 替换到 response.data，方便调用方直接使用
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (response as any).data = apiData.data;
    return response;
  },
  (error: AxiosError<ApiResponse<unknown>>) => {
    let message = '网络错误，请稍后重试';
    if (error.response) {
      const status = error.response.status;
      const apiMsg = error.response.data?.message;
      if (apiMsg) {
        message = apiMsg;
      } else if (status === 404) {
        message = '请求的资源不存在';
      } else if (status === 500) {
        message = '服务器内部错误';
      } else if (status === 403) {
        window.dispatchEvent(new Event('deepflow:forbidden'));
        message = '无权访问该资源';
      } else if (status === 401) {
        const currentToken = localStorage.getItem('deepflow_token');
        if (currentToken && error.config?.headers?.Authorization === `Bearer ${currentToken}` &&
          !error.config?.url?.endsWith('/auth/login')) {
          window.dispatchEvent(new Event('deepflow:unauthorized'));
        }
        message = '未授权，请重新登录';
      } else if (status === 400 || status === 422) {
        message = error.response.data?.detail === '原密码不正确'
          ? '原密码不正确' : '输入内容不符合要求，请检查后重试';
      } else if (status === 409) {
        message = '数据已被更新，请重新加载后重试';
      } else if (status === 429) {
        message = '请求过于频繁，请稍后重试';
      } else if (status === 503) {
        message = '服务暂不可用，请稍后重试';
      }
    } else if (error.request) {
      message = '无法连接到服务器，请检查网络';
    }
    // 统一将处理后的错误信息附加到 error 对象
    const enhancedError = new Error(message);
    enhancedError.name = 'ApiError';
    return Promise.reject(enhancedError);
  }
);

export default apiClient;
